import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Platform,
  ActivityIndicator,
  TextInput,
  Switch,
  Alert,
} from 'react-native';
import { useRouter } from 'expo-router';
import {
  ChevronLeft,
  User,
  Mail,
  Phone,
  Bell,
  Lock,
  Save,
  LogOut,
} from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { supabase } from '@/utils/supabase';
import { Mentor } from '@/types/mentor';
import { AccessibilityButton } from '@/components/AccessibilityButton';

export default function MentorSettingsScreen() {
  const router = useRouter();
  const [mentor, setMentor] = useState<Mentor | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [bio, setBio] = useState('');
  const [emailNotifications, setEmailNotifications] = useState(true);

  useEffect(() => {
    loadMentorData();
  }, []);

  const loadMentorData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        router.replace('/');
        return;
      }

      const { data: mentorData } = await supabase
        .from('mentors')
        .select('*')
        .eq('email', user.email)
        .single();

      if (mentorData) {
        setMentor(mentorData);
        setFullName(mentorData.full_name);
        setPhone(mentorData.phone || '');
        setBio(mentorData.bio || '');
        setEmailNotifications(mentorData.email_notifications);
      }
    } catch (error) {
      console.error('Error loading mentor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!mentor || !fullName.trim()) {
      Alert.alert('שגיאה', 'נא למלא את כל השדות החובה');
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('mentors')
        .update({
          full_name: fullName.trim(),
          phone: phone.trim(),
          bio: bio.trim(),
          email_notifications: emailNotifications,
        })
        .eq('id', mentor.id);

      if (error) {
        Alert.alert('שגיאה', 'לא הצלחנו לשמור את השינויים');
      } else {
        Alert.alert('הצלחה', 'הפרטים נשמרו בהצלחה');
        loadMentorData();
      }
    } catch (error) {
      console.error('Error saving mentor data:', error);
      Alert.alert('שגיאה', 'משהו השתבש, נסה שוב');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    Alert.alert(
      'שינוי סיסמה',
      'אנחנו נשלח לך קישור לאיפוס סיסמה באימייל',
      [
        { text: 'ביטול', style: 'cancel' },
        {
          text: 'שלח',
          onPress: async () => {
            if (!mentor) return;

            try {
              const { error } = await supabase.auth.resetPasswordForEmail(
                mentor.email
              );

              if (error) {
                Alert.alert('שגיאה', 'לא הצלחנו לשלוח את הקישור');
              } else {
                Alert.alert('נשלח', 'בדוק את האימייל שלך לקישור לאיפוס סיסמה');
              }
            } catch (error) {
              Alert.alert('שגיאה', 'משהו השתבש');
            }
          },
        },
      ]
    );
  };

  const handleLogout = () => {
    Alert.alert('התנתקות', 'אתה בטוח שברצונך להתנתק?', [
      { text: 'ביטול', style: 'cancel' },
      {
        text: 'התנתק',
        style: 'destructive',
        onPress: async () => {
          await supabase.auth.signOut();
          router.replace('/');
        },
      },
    ]);
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>טוען הגדרות...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>הגדרות</Text>
        <Pressable onPress={() => router.back()} style={styles.backButton}>
          <ChevronLeft color={theme.colors.text} size={24} />
        </Pressable>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>פרטים אישיים</Text>

          <View style={styles.inputGroup}>
            <View style={styles.inputHeader}>
              <User color={theme.colors.textLight} size={20} />
              <Text style={styles.inputLabel}>שם מלא *</Text>
            </View>
            <TextInput
              style={styles.input}
              value={fullName}
              onChangeText={setFullName}
              placeholder="הזן שם מלא"
              placeholderTextColor={theme.colors.textLight}
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputHeader}>
              <Mail color={theme.colors.textLight} size={20} />
              <Text style={styles.inputLabel}>אימייל</Text>
            </View>
            <TextInput
              style={[styles.input, styles.inputDisabled]}
              value={mentor?.email}
              editable={false}
            />
            <Text style={styles.inputHint}>
              לא ניתן לשנות את האימייל
            </Text>
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputHeader}>
              <Phone color={theme.colors.textLight} size={20} />
              <Text style={styles.inputLabel}>טלפון</Text>
            </View>
            <TextInput
              style={styles.input}
              value={phone}
              onChangeText={setPhone}
              placeholder="050-1234567"
              placeholderTextColor={theme.colors.textLight}
              keyboardType="phone-pad"
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.inputHeader}>
              <User color={theme.colors.textLight} size={20} />
              <Text style={styles.inputLabel}>ביוגרפיה</Text>
            </View>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={bio}
              onChangeText={setBio}
              placeholder="ספר על עצמך..."
              placeholderTextColor={theme.colors.textLight}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>התראות</Text>

          <View style={styles.settingRow}>
            <View style={styles.settingInfo}>
              <Bell color={theme.colors.textLight} size={20} />
              <View style={styles.settingText}>
                <Text style={styles.settingLabel}>התראות באימייל</Text>
                <Text style={styles.settingDescription}>
                  קבל התראה כשמגיע ליד חדש
                </Text>
              </View>
            </View>
            <Switch
              value={emailNotifications}
              onValueChange={setEmailNotifications}
              trackColor={{
                false: theme.colors.border,
                true: theme.colors.primary,
              }}
              thumbColor="#FAF5EA"
            />
          </View>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>אבטחה</Text>

          <Pressable
            style={({ pressed }) => [
              styles.actionRow,
              pressed && styles.actionRowPressed,
            ]}
            onPress={handleChangePassword}
          >
            <Lock color={theme.colors.textLight} size={20} />
            <Text style={styles.actionText}>שינוי סיסמה</Text>
          </Pressable>
        </View>

        <Pressable
          style={({ pressed }) => [
            styles.saveButton,
            pressed && styles.saveButtonPressed,
            saving && styles.saveButtonDisabled,
          ]}
          onPress={handleSave}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator color="#FAF5EA" />
          ) : (
            <>
              <Save color="#FAF5EA" size={20} />
              <Text style={styles.saveButtonText}>שמור שינויים</Text>
            </>
          )}
        </Pressable>

        <Pressable
          style={({ pressed }) => [
            styles.logoutButton,
            pressed && styles.logoutButtonPressed,
          ]}
          onPress={handleLogout}
        >
          <LogOut color="#FF6B6B" size={20} />
          <Text style={styles.logoutButtonText}>התנתק</Text>
        </Pressable>
      </ScrollView>

      <AccessibilityButton />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: theme.colors.background,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.md,
  },
  loadingText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.textLight,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    paddingTop: Platform.OS === 'ios' ? 50 : theme.spacing.md,
    backgroundColor: theme.colors.backgroundLight,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: 24,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  backButton: {
    padding: theme.spacing.xs,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: theme.spacing.lg,
    paddingBottom: theme.spacing.xl * 2,
  },
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  },
  inputGroup: {
    marginBottom: theme.spacing.md,
  },
  inputHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.xs,
  },
  inputLabel: {
    fontSize: 14,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
  },
  input: {
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.md,
    fontSize: 16,
    fontFamily: theme.fonts.regular,
    color: theme.colors.text,
    textAlign: 'right',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  inputDisabled: {
    opacity: 0.6,
    backgroundColor: theme.colors.background,
  },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top',
  },
  inputHint: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
    marginTop: theme.spacing.xs,
    textAlign: 'right',
  },
  settingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  settingInfo: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  settingText: {
    flex: 1,
  },
  settingLabel: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 13,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  actionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  actionRowPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.8,
  },
  actionText: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.md,
    marginTop: theme.spacing.xl,
  },
  saveButtonPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.9,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: '#FAF5EA',
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    backgroundColor: theme.colors.secondary,
    borderRadius: theme.borderRadius.lg,
    paddingVertical: theme.spacing.md,
    marginTop: theme.spacing.md,
    borderWidth: 2,
    borderColor: '#FF6B6B',
  },
  logoutButtonPressed: {
    transform: [{ scale: 0.98 }],
    opacity: 0.8,
  },
  logoutButtonText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: '#FF6B6B',
  },
});
